import {
  companyagreementApi,
  ProductTypologyEnum,
  RelationshipEnum,
  TargetPersonEnum,
  type membershipApi,
  type shoppingApi,
  type TargetPersonEnumType,
  type UserFamilyMember,
} from 'app/src/services/welfareportal'
import type { PrivacyConsentEntry } from 'app/src/services/welfareportal/shopping-api'
import { Persona } from 'app/src/shared/components/Persona'
import { useWebAppContext } from 'app/src/shared/hooks'
import { cloneDeep } from 'lodash'
import { Button } from 'primereact/button'
import { Checkbox, type CheckboxChangeEvent } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect, type MultiSelectChangeEvent } from 'primereact/multiselect'
import type { SelectItem } from 'primereact/selectitem'
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

export type ProductActivationModalProps = {
  typeOfBeneficiaries?: TargetPersonEnumType
  isMultiple?: boolean
  user?: membershipApi.MembershipExtended
  product: companyagreementApi.Product
  finalUsageList: string[]
  personalData?: string[]
  personalDataDisclaimer?: string
  targetPersonDisclaimer?: string
  apiConfiguration?: companyagreementApi.ApiConfiguration
  familyMemberOptionValue: SelectItem[]
  setFamilyMemberOptionValue: React.Dispatch<React.SetStateAction<SelectItem[]>>
  errorMsg?: JSX.Element
  renderAnagraficaBtn?: (btnLabel: string) => JSX.Element
  setFinalUsage?: React.Dispatch<React.SetStateAction<string | undefined>>
  finalUsage: string | undefined
  setFinalUsageIsSelected?: React.Dispatch<React.SetStateAction<boolean>>
  finalUsageIsSelected: boolean
  setPrivacyItems: React.Dispatch<React.SetStateAction<{ [key: string]: PrivacyConsentEntry }>>
  privacyItems: { [key: string]: PrivacyConsentEntry }
  setProductActivationModalConfirmDisabled?: React.Dispatch<React.SetStateAction<boolean>>
  Continue?: (
    familyMembers: UserFamilyMember[],
    userSelected: boolean,
    productAdditionalInfo?: shoppingApi.ProductAdditionalInfo,
    privacyConsent?: { [key: string]: boolean },
  ) => void
  onChangeFinalUsage: (item: SelectItem) => void
}
export function ProductActivationModal(props: ProductActivationModalProps): ReactElement {
  const context = useWebAppContext()
  const { t } = useTranslation('productDetail', { keyPrefix: 'ProductDetail.productActivationModal' })
  const [familyMemberOptionList, setFamilyMemberOptionList] = useState<SelectItem[]>([])
  const [selectedFamilyMemberKeys, setSelectedFamilyMemberKeys] = useState<string[]>([])
  // const [familyMemberOptionValue, setFamilyMemberOptionValue] = useState<SelectItem[]>([])
  const [selectedFamilyMemberOptionValue, setSelectedFamilyMemberOptionValue] = useState<UserFamilyMember[]>([]) //MIA
  const [, setShowFiscalCode] = useState<boolean>(false)
  const dropdownFinalUsageRef = useRef<Dropdown>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  // const [finalUsage, setFinalUsage] = useState<string>()
  // const [finalUsageIsSelected, setFinalUsageIsSelected] = useState<boolean>(false)
  // const [privacyItems, setPrivacyItems] = useState<{ [key: string]: boolean }>({})
  useEffect(() => {
    getOptions(props.user?.membershipInfo?.userFamilyMembers)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.user?.membershipInfo?.userFamilyMembers])
  useEffect(() => {
    if (props.product?.privacyList) {
      const _privacyItems: { [key: string]: PrivacyConsentEntry } = {}
      props.product.privacyList.forEach((privacyEntry) => {
        if (context.userInfo.welfareCampaign.documents.find((doc) => doc.type === privacyEntry.privacyId)) {
          _privacyItems[privacyEntry.privacyId] = {
            accepted: false,
            documentId: context.userInfo.welfareCampaign.documents.find((doc) => doc.type === privacyEntry.privacyId)?.documentId,
          }
        }
      })
      props.setPrivacyItems(_privacyItems)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.product?.privacyList])

  const finalOptionList: SelectItem[] = useMemo(() => {
    const updatedOptions =
      props.finalUsageList?.map((el: string) => {
        const option = { label: el, title: el, value: el }
        return option
      }) ?? []

    if (updatedOptions?.length === 1) {
      props.setFinalUsage!(updatedOptions[0].value)
      props.setFinalUsageIsSelected!(true)
    }

    return updatedOptions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.finalUsageList])

  useEffect(() => {
    if (!familyMemberOptionList || familyMemberOptionList.length === 0) {
      props.setProductActivationModalConfirmDisabled!(true)
      return
    }

    //mi assicura user:true in caso di utente come unico beneficiario
    if (familyMemberOptionList && familyMemberOptionList.length === 1)
      props.setFamilyMemberOptionValue([familyMemberOptionList[0]])

    let dis: boolean = false

    if (familyMemberOptionList.length > 1) dis = selectedFamilyMemberOptionValue?.length == 0

    //Privacy Items
    if (props.product?.privacyList && !props.privacyItems) {
      dis = true
    } else {
      if (props.product?.privacyList && props.privacyItems) {
        props.product?.privacyList.forEach((privacyEntry) => {
          const privacyItem = props.privacyItems[privacyEntry.privacyId]
          if (privacyEntry.mandatory && privacyItem && !privacyItem.accepted) dis = true
        })
      }
    }

    props.setProductActivationModalConfirmDisabled!(dis)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFamilyMemberOptionValue, selectedFamilyMemberKeys, familyMemberOptionList, props.privacyItems])

  const hasDuplicates = (arr: Array<string>) => new Set(arr).size !== arr.length

  const getBeneficiarySelectionInfo = () => {
    switch (props.typeOfBeneficiaries) {
      case TargetPersonEnum.All:
      case TargetPersonEnum.AllAndAdultWithPrivacy:
        return t('beneficiarySelectionInfo.all')
      case TargetPersonEnum.DependentChildrenAndSpouse:
        return t('beneficiarySelectionInfo.dependentChildrenAndSpouse')
      case TargetPersonEnum.EmployeeAndFamilyMemberDependent:
        return t('beneficiarySelectionInfo.employeeAndFamilyMemberDependent')
      case TargetPersonEnum.FamilyMember:
        return t('beneficiarySelectionInfo.familyMember')
      case TargetPersonEnum.FamilyMemberDependent:
        return t('beneficiarySelectionInfo.familyMemberDependent')
      default:
        return ''
    }
  }
  function getOptions(members?: UserFamilyMember[]) {
    const displayName = `${context.userInfo.firstName} ${context.userInfo.lastName}`

    const userNameList =
      props.typeOfBeneficiaries === TargetPersonEnum.All ||
      props.typeOfBeneficiaries === TargetPersonEnum.Employee ||
      props.typeOfBeneficiaries === TargetPersonEnum.AllAndAdultWithPrivacy ||
      props.typeOfBeneficiaries === TargetPersonEnum.EmployeeAndFamilyMemberDependent
        ? [displayName] //displayName
        : []

    const options: SelectItem[] =
      props.typeOfBeneficiaries === TargetPersonEnum.All ||
      props.typeOfBeneficiaries === TargetPersonEnum.Employee ||
      props.typeOfBeneficiaries === TargetPersonEnum.AllAndAdultWithPrivacy ||
      props.typeOfBeneficiaries === TargetPersonEnum.EmployeeAndFamilyMemberDependent
        ? [
            {
              //key: props.user.userId,
              label: displayName,
              title: props.user?.membershipInfo?.userFiscalCode,
              value: {
                familyMemberId: props.user?.membershipInfo?.userId,
                firstName: displayName,
                lastName: '',
                fiscalCode: props.user?.membershipInfo?.userFiscalCode,
                relationship: null,
                taxDependent: true,
                signature: null,
              },
              // disabled: false
            },
          ]
        : []

    members?.forEach((member: UserFamilyMember) => {
      //start name deleted
      let option: SelectItem = {}
      let optionAssigned: boolean = false
      switch (props.typeOfBeneficiaries) {
        case TargetPersonEnum.All:
        case TargetPersonEnum.FamilyMember:
        case TargetPersonEnum.AllAndAdultWithPrivacy:
          option = { label: `${member.firstName} ${member.lastName}`, title: member.fiscalCode, value: member }
          optionAssigned = true
          break
        case TargetPersonEnum.FamilyMemberDependent:
        case TargetPersonEnum.EmployeeAndFamilyMemberDependent:
          if (member.taxDependent) {
            option = { label: `${member.firstName} ${member.lastName}`, title: member.fiscalCode, value: member }
            optionAssigned = true
          }
          break
        case TargetPersonEnum.DependentChildrenAndSpouse:
          if (
            (member.taxDependent && member.relationship === RelationshipEnum.FiglioFiglia.toString()) ||
            member.relationship === RelationshipEnum.Coniuge.toString()
          ) {
            option = { label: `${member.firstName} ${member.lastName}`, title: member.fiscalCode, value: member }
            optionAssigned = true
          }
          break
        default:
          optionAssigned = false
          break
      }

      if (optionAssigned) {
        options.push(option)
        userNameList.push(`${member.firstName} ${member.lastName}`)
      }

      //end name deleted
    })

    setFamilyMemberOptionList(options)
    setShowFiscalCode(hasDuplicates(userNameList))
  }

  const onChangeMultipleSelectFamilyMember = (event: MultiSelectChangeEvent): void => {
    if (event) {
      const itemSelected: boolean = selectedFamilyMemberKeys.find((x) => x === event.selectedOption?.value?.familyMemberId)
        ? true
        : false

      setSelectedFamilyMemberKeys(
        !itemSelected
          ? [...selectedFamilyMemberKeys, event.selectedOption?.value?.familyMemberId as string]
          : selectedFamilyMemberKeys.filter((key) => key !== event.selectedOption?.value?.familyMemberId),
      )
      props.setFamilyMemberOptionValue(
        !itemSelected
          ? [...props.familyMemberOptionValue, event.selectedOption]
          : props.familyMemberOptionValue.filter((v) => v.value?.familyMemberId !== event.selectedOption?.value?.familyMemberId),
      )
      setSelectedFamilyMemberOptionValue(
        //MIA
        !itemSelected
          ? [...selectedFamilyMemberOptionValue, event.selectedOption?.value]
          : selectedFamilyMemberOptionValue.filter((v) => v?.familyMemberId !== event.selectedOption?.value?.familyMemberId),
      )
    }
  }

  const onChangeSelectFamilyMember = (item: SelectItem): void => {
    if (item) {
      const itemSelected: boolean = selectedFamilyMemberKeys.find((x) => x === item.value?.familyMemberId) ? true : false

      if (props.isMultiple) {
        setSelectedFamilyMemberKeys(
          !itemSelected
            ? [...selectedFamilyMemberKeys, item.value?.familyMemberId as string]
            : selectedFamilyMemberKeys.filter((key) => key !== item.value?.familyMemberId),
        )
        props.setFamilyMemberOptionValue(
          !itemSelected
            ? [...props.familyMemberOptionValue, item]
            : props.familyMemberOptionValue.filter((v) => v.value?.familyMemberId !== item.value?.familyMemberId),
        )
        setSelectedFamilyMemberOptionValue(
          //MIA
          !itemSelected
            ? [...selectedFamilyMemberOptionValue, item.value]
            : selectedFamilyMemberOptionValue.filter((v) => v?.familyMemberId !== item.value?.familyMemberId),
        )
      } else {
        props.setFamilyMemberOptionValue([item])
        setSelectedFamilyMemberOptionValue([item.value])
      }
    }
  }

  function renderHeaderNoFamilyMembers() {
    return (
      <div>
        <h2 className='text-xl font-bold'>{t('headerNoFamilyMembers.title')}</h2>
        {props.renderAnagraficaBtn && (
          <p className='mt-2'>
            {t('headerNoFamilyMembers.p1')} {props.renderAnagraficaBtn(t('headerNoFamilyMembers.btnAnagraficaSectionLabel'))},{' '}
            {t('headerNoFamilyMembers.p2')}
          </p>
        )}
      </div>
    )
  }

  function renderHeaderNotEmployee() {
    return (
      <div>
        <h2 className='text-xl font-bold'>{t('headerNotEmployee.title')}</h2>
        <p className='mt-2'>
          {t('headerNotEmployee.p1')} {props.isMultiple ? t('headerNotEmployee.p2') : t('headerNotEmployee.p3')}{' '}
          {t('headerNotEmployee.p4')} {getBeneficiarySelectionInfo()}
          {props.finalUsageList && props.finalUsageList.length > 1 ? t('headerNotEmployee.p5') : ''}. {t('headerNotEmployee.p6')}{' '}
          {props.renderAnagraficaBtn!(t('headerNotEmployee.btnAnagraficaSectionLabel'))}
        </p>
        {props.targetPersonDisclaimer && <p>{props.targetPersonDisclaimer}</p>}
      </div>
    )
  }

  function renderHeaderFinalUsageTitle() {
    return (
      <div>
        <h2 className='text-xl font-bold'>
          {props.finalUsageList.length > 1 ? t('headerFinalUsage.title1') : t('headerFinalUsage.title2')}
        </h2>
        {props.finalUsageList.length > 1 && <p>{t('headerFinalUsage.p')}</p>}
      </div>
    )
  }

  function renderHeaderConfirmTitle() {
    return (
      <div>
        <h2 className='text-xl font-bold'>{t('headerConfirmTitle.title')}</h2>
      </div>
    )
  }

  function renderHeaderModal() {
    // return renderFinalUsageTitle()
    if (familyMemberOptionList.length === 0) return renderHeaderNoFamilyMembers()
    else if (props.typeOfBeneficiaries !== TargetPersonEnum.Employee) return renderHeaderNotEmployee()
    else if (props.finalUsageList && props.finalUsageList.length > 0) return renderHeaderFinalUsageTitle()
    else return renderHeaderConfirmTitle()
  }

  const showPersonas = (options: SelectItem[], withoutButton?: boolean) => {
    return (
      <div className='flex flex-wrap gap-1'>
        {options.map((el) => (
          <Persona
            key={el.value?.familyMemberId}
            label={`${el.value?.firstName} ${el.value?.lastName}`}
            btnCancelAriaLabel={t('showPersonas.btnCancelAriaLabel')}
            onClickCancelButton={() => onChangeSelectFamilyMember(el)}
            btnCancelVisibility={!withoutButton && props.isMultiple}
          />
        ))}
      </div>
    )
  }
  const dropDownValueTemplateFamilyMember = (opt: SelectItem) => {
    if (opt)
      return (
        <div>
          <Persona
            label={`${opt?.value?.firstName?.toUpperCase()} ${opt?.value?.lastName?.toUpperCase()}`}
            btnCancelVisibility={false}
          />
        </div>
      )
    else return <span className='text-[18px]'>Seleziona ...</span>
  }

  // const multipleSelectedItemTemplate = (opt: UserFamilyMember) => {
  // if (opt)
  //   return (
  //     <div>
  //       <Persona label={`${opt?.firstName?.toUpperCase()} ${opt?.lastName?.toUpperCase()}`} btnCancelVisibility={false} />
  //     </div>
  //   )
  // else return <span className='text-[18px]'>Seleziona ...</span>
  // }

  const dropDownItemTemplateFamilyMember = (opt: SelectItem) => {
    return (
      <div className='align-items-center flex flex-row items-center px-[10px] py-[2px]'>
        {opt?.value?.firstName?.toUpperCase()} {opt?.value?.lastName?.toUpperCase()}
      </div>
    )
  }

  function renderBodyFamilyMemberSelection() {
    if (familyMemberOptionList.length === 1) {
      return (
        <div className='col-span-12 grid grid-cols-12'>
          <p className='col-span-12 my-3'>{t('bodyFamilyMemberSelection.singleMemberTitle')}</p>
          <div className='col-span-12'>{showPersonas(familyMemberOptionList, true)}</div>
        </div>
      )
    } else if (props.isMultiple) {
      // } else {
      return (
        <div className='col-span-12 grid grid-cols-12'>
          <p className='col-span-12 my-3'>{t('bodyFamilyMemberSelection.multipleMemberTitle')}</p>
          <MultiSelect
            className='col-span-12 border-[var(--primary-color)] hover:bg-[#f0f8ff] md:col-span-8'
            itemTemplate={dropDownItemTemplateFamilyMember}
            // selectedItemTemplate={multipleSelectedItemTemplate}
            value={selectedFamilyMemberOptionValue} //{familyMemberOptionValue}
            onChange={(item) => onChangeMultipleSelectFamilyMember(item)}
            inputMode={'none'}
            placeholder={t('bodyFamilyMemberSelection.multiselectPlaceholder')}
            emptyMessage={t('bodyFamilyMemberSelection.multiselectEmptyMessage')}
            fixedPlaceholder={true}
            id='multiselectDropDownFamilyMember'
            options={familyMemberOptionList}
            pt={{ item: { className: 'hover:bg-[#f0f8ff]' }, closeButton: { className: 'hidden' } }}
            showSelectAll={false}
            showClear={false}
            closeIcon={null}
          />
          <div className='col-span-12 mt-3'>{showPersonas(props.familyMemberOptionValue)}</div>
        </div>
      )
    } else {
      return (
        <div className='col-span-12 grid grid-cols-12'>
          <p className='col-span-12 my-3'>{t('bodyFamilyMemberSelection.singleMemberTitle')}</p>
          <Dropdown
            className='col-span-12 rounded-none border-x-0 border-t-0 border-[var(--primary-color)] border-black hover:bg-[#f0f8ff] md:col-span-8'
            itemTemplate={dropDownItemTemplateFamilyMember}
            valueTemplate={dropDownValueTemplateFamilyMember}
            value={selectedFamilyMemberOptionValue?.[0]}
            onChange={(item) => onChangeSelectFamilyMember(item)}
            inputMode={'none'}
            placeholder={t('bodyFamilyMemberSelection.dropdownPlaceholder')}
            id='singleDropDownFamilyMember'
            options={familyMemberOptionList}
            pt={{ item: { className: 'hover:bg-[#f0f8ff]' }, itemLabel: { className: 'h-30px' }, input: { className: 'py-2' } }}
          />
        </div>
      )
    }
  }
  function renderFinalUsageSelection() {
    if (props.finalUsageList && props.finalUsageList.length > 0)
      return (
        <div className='col-span-12 mt-[20px] grid grid-cols-12'>
          <p className='col-span-12 my-3'>{t('finalUsageSelection.title')}</p>
          {props.finalUsageList.length > 1 && (
            <Dropdown
              className='col-span-12 rounded-none border-x-0 border-t-0 border-[var(--primary-color)] border-black hover:bg-[#f0f8ff] md:col-span-8'
              value={props.finalUsage}
              onChange={(item) => props.onChangeFinalUsage(item)}
              inputMode={'search'}
              editable
              placeholder={t('finalUsageSelection.dropdownPlaceholder')}
              id='singleDropDownFinalUsage'
              options={finalOptionList}
              ref={dropdownFinalUsageRef}
              pt={{
                item: { className: 'hover:bg-[#f0f8ff]' },
                itemLabel: { className: 'h-30px' },
                input: {
                  className: 'cursor-pointer py-2',
                  onClick: () => {
                    if (dropdownFinalUsageRef?.current) {
                      if (isDropdownOpen) dropdownFinalUsageRef.current.hide()
                      else dropdownFinalUsageRef.current.show()
                      setIsDropdownOpen(!isDropdownOpen)
                    }
                  },
                },
              }}
            />
          )}
          {props.finalUsageList.length == 1 && (
            <p className='col-span-12'>
              <b>{props.finalUsage}</b>
            </p>
          )}
        </div>
      )
  }
  function renderPersonalDataDiscalimer() {
    if (props.personalData && props.personalData.length)
      return (
        <div className='col-span-12 mt-8'>
          <p className='mb-1'>
            {(props.product.typology as string) === (ProductTypologyEnum.GoodAndService as unknown as string) && (
              <b>{t('personalDataDiscalimer.title')}</b>
            )}
            <b> {t('personalDataDiscalimer.description')}</b>
          </p>
          <ul className='pl-10' style={{ listStyleType: 'disc' }}>
            {props.personalData.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
          {props.personalDataDisclaimer && <p className='mt-5'> {props.personalDataDisclaimer}</p>}
        </div>
      )
  }
  function renderPrivacyList() {
    if (familyMemberOptionList.length > 0) {
      if (props.product?.privacyList && props.product.privacyList.length > 0)
        return (
          <div className='col-span-12 mt-[20px]'>
            <p>{t('privacyList.title')}</p>
            {props.product.privacyList.map((privacyEntry: { privacyId: string; mandatory: boolean }) => {
              // Cerca il documento corrispondente al privacyId
              const privacyDoc = context.userInfo.welfareCampaign.documents.find((doc) => doc.type === privacyEntry.privacyId)

              if (!privacyDoc) return null

              return (
                <div key={privacyEntry.privacyId} className='col-span-12 mt-5 flex flex-row items-start'>
                  <Checkbox
                    className='mt-1'
                    pt={{ root: { className: 'w-5 h-5' }, box: { className: 'w-5 h-5' } }}
                    inputId={privacyEntry.privacyId}
                    checked={props.privacyItems ? props.privacyItems[privacyEntry.privacyId].accepted : false}
                    onChange={(evt: CheckboxChangeEvent) => {
                      const _privacyItemsTemp: { [key: string]: PrivacyConsentEntry } = cloneDeep(props.privacyItems)
                      _privacyItemsTemp[privacyEntry.privacyId].accepted = evt.checked ? evt.checked : false
                      console.log('privacyItems', _privacyItemsTemp)
                      props.setPrivacyItems(_privacyItemsTemp)
                    }}
                  />
                  <label htmlFor={privacyEntry.privacyId} className='ml-2 cursor-pointer' style={{ userSelect: 'none' }}>
                    <div>
                      <div className='text-sm font-bold'>
                        {privacyDoc.title || privacyDoc.name}
                        {privacyEntry.mandatory ? '*' : ''}
                      </div>
                      {privacyDoc.body && <div className='text-sm font-normal'>{privacyDoc.body}</div>}
                      <Button
                        label={t('privacyList.btnInformativaLabel')}
                        link
                        className='mt-[5px] p-0 text-left'
                        pt={{
                          label: { className: 'text-sm text-[#323130] underline font-normal leading-[22px]' },
                        }}
                        onClick={
                          privacyDoc.url
                            ? () => {
                                window.open(privacyDoc.url)
                              }
                            : undefined
                        }
                      />
                    </div>
                  </label>
                </div>
              )
            })}
          </div>
        )
    } else return <div></div>
  }
  function renderBodyModal() {
    return (
      <div className='mt-[20px] grid grid-cols-12'>
        {renderBodyFamilyMemberSelection()}
        {renderFinalUsageSelection()}
        {renderPersonalDataDiscalimer()}
        {renderPrivacyList()}
      </div>
    )
  }

  return (
    <div className='grid grid-cols-1'>
      {renderHeaderModal()}
      {renderBodyModal()}
    </div>
  )
}
